import type { Context } from "./models/context";
import type { AutomationAction } from "@/hooks/firestore/models/baseAutomation";

export const checkExceedsTimesAllowedToCompletePerEvent = (
  context: Context,
  requirement: AutomationAction,
) => {
  if (context.selection.allocationId === context.allocation.id) return false;

  const timesAllowedToComplete =
    requirement.userDrivenAttributes?.timesAllowedToCompletePerEvent!;
  const automationId = context.automation.automationId;
  const eventIdUsages = context.allocation.attributes?.eventIdUsages || {}; // record of numbers per eventId

  const currentCount = context.selection.outcomes.reduce(
    (totalUsages, outcome) => {
      const eventId = outcome.eventId;

      // 1. Get usages from automation for this eventId
      const pastEventIdUsagesForCurrentEvent = eventIdUsages[eventId] ?? 0;

      // 2. get usages from betslip context
      const futureEventIdUsagesForCurrentEvent =
        (context.usages[automationId]?.[eventId] || []).length ?? 0;

      totalUsages +=
        pastEventIdUsagesForCurrentEvent + futureEventIdUsagesForCurrentEvent;

      return totalUsages;
    },
    0,
  );

  return currentCount >= timesAllowedToComplete;
};

const markAsUsed = (context: Context) => {
  const { allocation, usedAllocations } = context;
  let usedAtOutcomeId = null;
  let usedAtEventId = null;

  if (typeof usedAllocations[allocation.id] !== "undefined") {
    // this allocation is already used in on a different selection
    // find out which one is it, and set usedAt to the outcomeId of that selection
    usedAtOutcomeId = usedAllocations[allocation.id].outcomeId;
    usedAtEventId = usedAllocations[allocation.id].eventId;

    return {
      ...allocation,
      usedAtOutcomeId,
      usedAtEventId,
    };
  }

  const requirement = (context.automation.requirements || []).find(
    (requirement) =>
      ["NON_SEM_BET_STAKE", "SEM_BET_STAKE"].includes(requirement.type),
  );

  if (!requirement) return allocation;

  const requiresCheck =
    typeof requirement.userDrivenAttributes?.timesAllowedToCompletePerEvent !==
    "undefined";

  if (
    requiresCheck &&
    checkExceedsTimesAllowedToCompletePerEvent(context, requirement)
  ) {
    // this allocation exceeds the rules of times allowed to complete
    // we can grab any allocation used from this automation and set it as the one that is used
    const eventId = context.selection.eventIds[0];
    const usedAllocationId =
      context.usages[context.automation.automationId]?.[eventId]?.[0] ?? null;

    if (!usedAllocationId) return allocation;

    usedAtOutcomeId = usedAllocations[usedAllocationId].outcomeId ?? null;
    usedAtEventId = usedAllocations[usedAllocationId].eventId ?? null;
  }

  return {
    ...allocation,
    usedAtOutcomeId,
    usedAtEventId,
  };
};

export { markAsUsed };
